import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingUp, TrendingDown, BarChart3, AlertCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";

interface TransactionWithCategory {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  categoria?: {
    nome: string;
    cor: string;
  };
}

interface TrendsAnalysisReportProps {
  transactions: TransactionWithCategory[];
  selectedDate?: Date;
}

interface TrendData {
  month: string;
  fullMonth: string;
  [key: string]: string | number;
}

export function TrendsAnalysisReport({ transactions, selectedDate }: TrendsAnalysisReportProps) {
  const [analysisType, setAnalysisType] = useState<'saida' | 'entrada'>('saida');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showTop, setShowTop] = useState(5);
  const [timeRange, setTimeRange] = useState(6); // meses
  const [yearsToShow, setYearsToShow] = useState(2);
  const [showAllYears, setShowAllYears] = useState(false);

  const trendsData = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return { monthlyData: [], categories: [], insights: [] };
    }

    // Calcular período baseado na seleção
    const endDate = selectedDate || new Date();
    let startDate: Date;
    
    if (showAllYears) {
      // Filtrar transações válidas para determinar o período
      const validTransactions = transactions.filter(t => !t.ignorar_transacao);
      if (validTransactions.length > 0) {
        const oldestTransaction = validTransactions.reduce((oldest, current) => {
          return new Date(current.data) < new Date(oldest.data) ? current : oldest;
        });
        const oldestYear = new Date(oldestTransaction.data).getFullYear();
        startDate = new Date(oldestYear, 0, 1); // 1º de janeiro do ano mais antigo
      } else {
        startDate = new Date(endDate.getFullYear(), 0, 1); // 1º de janeiro do ano atual
      }
    } else {
      startDate = subMonths(endDate, timeRange - 1);
    }
    
    const months = eachMonthOfInterval({ start: startDate, end: endDate });

    // Filtrar transações por tipo
    const filteredTransactions = transactions.filter(t => 
      !t.ignorar_transacao && 
      t.tipo === analysisType && 
      (analysisType === 'entrada' ? t.foi_paga : true)
    );

    // Obter categorias mais relevantes
    const categoryTotals = filteredTransactions.reduce((acc, t) => {
      const categoryName = t.categoria?.nome || 'Sem categoria';
      acc[categoryName] = (acc[categoryName] || 0) + Number(t.valor);
      return acc;
    }, {} as Record<string, number>);

    const topCategories = Object.entries(categoryTotals)
      .sort(([,a], [,b]) => b - a)
      .slice(0, showTop)
      .map(([name]) => name);

    // Dados mensais por categoria
    const monthlyData: TrendData[] = months.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      
      const monthTransactions = filteredTransactions.filter(t => {
        const transactionDate = new Date(t.data);
        return transactionDate >= monthStart && transactionDate <= monthEnd;
      });
      
      const monthData: TrendData = {
        month: format(month, 'MMM', { locale: ptBR }),
        fullMonth: format(month, 'MMMM yyyy', { locale: ptBR })
      };
      
      topCategories.forEach(categoryName => {
        const categoryTotal = monthTransactions
          .filter(t => (t.categoria?.nome || 'Sem categoria') === categoryName)
          .reduce((sum, t) => sum + Number(t.valor), 0);
        monthData[categoryName] = categoryTotal;
      });
      
      return monthData;
    });

    // Calcular insights de tendências
    const insights = topCategories.map(categoryName => {
      const values = monthlyData.map(m => Number(m[categoryName]) || 0);
      const firstHalf = values.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
      const secondHalf = values.slice(3).reduce((a, b) => a + b, 0) / 3;
      const trend = secondHalf - firstHalf;
      const trendPercentage = firstHalf > 0 ? (trend / firstHalf) * 100 : 0;
      
      return {
        category: categoryName,
        trend: trendPercentage,
        direction: trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable',
        average: (firstHalf + secondHalf) / 2
      };
    });

    return { monthlyData, categories: topCategories, insights };
  }, [transactions, selectedDate, analysisType, showTop, timeRange, showAllYears]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getColorForCategory = (categoryName: string, index: number) => {
    const colors = [
      'hsl(var(--chart-1))',
      'hsl(var(--chart-2))',
      'hsl(var(--chart-3))',
      'hsl(var(--chart-4))',
      'hsl(var(--chart-5))'
    ];
    return colors[index % colors.length];
  };

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: TooltipPayload, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name || entry.dataKey}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {trendsData.insights.slice(0, 3).map((insight, index) => (
          <Card key={insight.category}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground truncate">
                {insight.category}
              </CardTitle>
              <div className={`p-2 rounded-md ${
                insight.direction === 'up' 
                  ? 'bg-destructive text-destructive-foreground'
                  : insight.direction === 'down'
                  ? 'bg-success text-success-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {insight.direction === 'up' ? (
                  <TrendingUp className="h-4 w-4" />
                ) : insight.direction === 'down' ? (
                  <TrendingDown className="h-4 w-4" />
                ) : (
                  <BarChart3 className="h-4 w-4" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-base font-bold">
                {formatCurrency(insight.average)}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={insight.direction === 'up' ? 'destructive' : insight.direction === 'down' ? 'default' : 'secondary'}>
                  {insight.direction === 'up' ? '+' : insight.direction === 'down' ? '-' : ''}
                  {Math.abs(insight.trend).toFixed(1)}%
                </Badge>
                <span className="text-xs text-muted-foreground">
                  vs. período anterior
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={analysisType === 'saida' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAnalysisType('saida')}
          >
            <TrendingDown className="h-4 w-4 mr-2" />
            Despesas
          </Button>
          <Button
            variant={analysisType === 'entrada' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAnalysisType('entrada')}
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Receitas
          </Button>
        </div>
        
        <div className="flex gap-2 items-center">
          <Select 
            value={showAllYears ? "all" : (timeRange >= 24 ? `${yearsToShow}y` : timeRange.toString())} 
            onValueChange={(value) => {
              if (value === "all") {
                setShowAllYears(true);
              } else if (value.endsWith('y')) {
                setShowAllYears(false);
                const years = Number(value.replace('y', ''));
                setYearsToShow(years);
                setTimeRange(years * 12);
              } else {
                setShowAllYears(false);
                setTimeRange(Number(value));
              }
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6">6 meses</SelectItem>
              <SelectItem value="12">12 meses</SelectItem>
              <SelectItem value="18">18 meses</SelectItem>
                <SelectItem value="2y">2 anos</SelectItem>
              <SelectItem value="3y">3 anos</SelectItem>
              <SelectItem value="4y">4 anos</SelectItem>
              <SelectItem value="5y">5 anos</SelectItem>
              <SelectItem value="all">Todos os anos</SelectItem>
            </SelectContent>
          </Select>
          
          <span className="text-sm text-muted-foreground">Top:</span>
          {[3, 5, 8].map(num => (
            <Button
              key={num}
              variant={showTop === num ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowTop(num)}
            >
              {num}
            </Button>
          ))}
        </div>
      </div>

      {/* Trends Chart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Análise de Tendências - {analysisType === 'saida' ? 'Despesas' : 'Receitas'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendsData.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => {
                    if (value >= 1000) {
                      return `R$ ${(value / 1000).toFixed(0)}k`;
                    }
                    return `R$ ${value}`;
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {trendsData.categories.map((category, index) => (
                  <Line
                    key={category}
                    type="monotone"
                    dataKey={category}
                    stroke={getColorForCategory(category, index)}
                    strokeWidth={2}
                    dot={{ fill: getColorForCategory(category, index), strokeWidth: 2, r: 4 }}
                    name={category}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Insights */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Insights Detalhados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {trendsData.insights.map((insight, index) => (
              <div key={insight.category} className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: getColorForCategory(insight.category, index) }}
                  />
                  <div>
                    <h4 className="font-medium">{insight.category}</h4>
                    <p className="text-sm text-muted-foreground">
                      Média: {formatCurrency(insight.average)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`flex items-center gap-2 ${
                    insight.direction === 'up' ? 'text-destructive' : 
                    insight.direction === 'down' ? 'text-success' : 'text-muted-foreground'
                  }`}>
                    {insight.direction === 'up' ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : insight.direction === 'down' ? (
                      <TrendingDown className="h-4 w-4" />
                    ) : (
                      <BarChart3 className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      {insight.direction === 'up' ? '+' : insight.direction === 'down' ? '-' : ''}
                      {Math.abs(insight.trend).toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {insight.direction === 'up' ? 'Aumentando' : 
                     insight.direction === 'down' ? 'Diminuindo' : 'Estável'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}